import { type FormEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { createProduct, getProduct, lookupBarcode, updateProduct } from '../../api/stock'
import { getMyPractices } from '../../api/staff'
import { BarcodeScanner } from '../../components/BarcodeScanner'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { FormField, inputClass, selectClass, textareaClass } from '../../components/ui/FormField'
import { useToast } from '../../components/ui/Toast'
import { useFetch } from '../../hooks/useFetch'

const UNITS = ['ml', 'tablet', 'capsule', 'vial', 'g', 'mg', 'IU', 'dose']
const SCHEDULES = [1, 2, 3, 4, 5, 6, 7, 8]

interface FormState {
  name: string
  generic_name: string
  manufacturer: string
  barcode: string
  unit: string
  schedule: string
  default_price: string
  notes: string
}

const EMPTY: FormState = {
  name: '', generic_name: '', manufacturer: '', barcode: '',
  unit: '', schedule: '', default_price: '', notes: '',
}

interface FieldErrors {
  name?: string
  unit?: string
  default_price?: string
}

export default function ProductForm() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const toast = useToast()

  const [form, setForm] = useState<FormState>(EMPTY)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [practiceId, setPracticeId] = useState<number | null>(null)
  const [practicesError, setPracticesError] = useState<string | null>(null)

  useEffect(() => {
    getMyPractices()
      .then((list) => {
        if (list.length === 0) { setPracticesError('No practices found.'); return }
        setPracticeId(list[0].id)
      })
      .catch(() => setPracticesError('Failed to load practice.'))
  }, [])

  const { data: existing, loading: loadingProduct, error: loadError } = useFetch(
    () => (isEdit && id ? getProduct(Number(id)) : Promise.resolve(null)),
    [id, isEdit],
  )

  useEffect(() => {
    if (!existing) return
    setForm({
      name: existing.name,
      generic_name: existing.generic_name ?? '',
      manufacturer: existing.manufacturer ?? '',
      barcode: existing.barcode ?? '',
      unit: existing.unit,
      schedule: existing.schedule != null ? String(existing.schedule) : '',
      default_price: existing.default_price ?? '',
      notes: existing.notes ?? '',
    })
  }, [existing])

  const set = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setForm((f) => ({ ...f, [field]: e.target.value }))

  const handleScan = async (code: string) => {
    if (!practiceId) return
    setForm((f) => ({ ...f, barcode: code }))
    try {
      const result = await lookupBarcode(practiceId, code)
      if (result.found && result.product) {
        toast.info('Product already exists in your practice.')
        void navigate(`/stock/products/${result.product.id}/edit`)
        return
      }
      if (result.suggestion) {
        setForm((f) => ({
          ...f,
          name: result.suggestion?.name ?? f.name,
        }))
        toast.info('Pre-filled from barcode database.')
      }
    } catch {
      toast.error('Could not look up barcode.')
    }
  }

  const validate = (): boolean => {
    const errors: FieldErrors = {}
    if (!form.name.trim()) errors.name = 'Name is required.'
    if (!form.unit) errors.unit = 'Unit is required.'
    if (form.default_price && isNaN(parseFloat(form.default_price))) {
      errors.default_price = 'Enter a valid price.'
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting || !practiceId) return
    setApiError(null)
    if (!validate()) return
    setSubmitting(true)
    const payload = {
      name: form.name.trim(),
      generic_name: form.generic_name.trim() || null,
      manufacturer: form.manufacturer.trim() || null,
      barcode: form.barcode.trim() || null,
      unit: form.unit,
      schedule: form.schedule !== '' ? Number(form.schedule) : null,
      default_price: form.default_price.trim() || null,
      notes: form.notes.trim() || null,
    }
    try {
      if (isEdit && id) {
        await updateProduct(Number(id), payload)
        toast.success('Product updated.')
      } else {
        await createProduct({ practice_id: practiceId, ...payload })
        toast.success('Product created.')
      }
      void navigate('/stock/products')
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setSubmitting(false)
    }
  }

  if (practicesError) return <div className="p-8"><ErrorBox>{practicesError}</ErrorBox></div>
  if (isEdit && loadingProduct) return <div className="text-text-2 p-8">Loading…</div>
  if (isEdit && loadError) return <div className="p-8"><ErrorBox>{loadError}</ErrorBox></div>

  return (
    <div>
      <h1 className="text-[1.4rem] font-bold text-text-heading mt-0 mb-7">
        {isEdit ? 'Edit Product' : 'New Product'}
      </h1>

      <Card className="max-w-[700px]">
        {apiError && <ErrorBox className="mb-5">{apiError}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <SectionTitle>Product Details</SectionTitle>

          <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
            <FormField label="Name" htmlFor="p_name" required error={fieldErrors.name}>
              <input
                id="p_name"
                className={inputClass(!!fieldErrors.name)}
                type="text"
                value={form.name}
                onChange={set('name')}
                disabled={submitting}
                autoFocus
              />
            </FormField>
            <FormField label="Generic name" htmlFor="p_generic">
              <input
                id="p_generic"
                className={inputClass()}
                type="text"
                value={form.generic_name}
                onChange={set('generic_name')}
                disabled={submitting}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
            <FormField label="Manufacturer" htmlFor="p_mfr">
              <input
                id="p_mfr"
                className={inputClass()}
                type="text"
                value={form.manufacturer}
                onChange={set('manufacturer')}
                disabled={submitting}
              />
            </FormField>
            <FormField label="Barcode" htmlFor="p_barcode">
              <div className="flex gap-2 items-center">
                <input
                  id="p_barcode"
                  className={`${inputClass()} flex-1`}
                  type="text"
                  value={form.barcode}
                  onChange={set('barcode')}
                  disabled={submitting}
                  placeholder="e.g. 9300601234567"
                />
                <BarcodeScanner onScan={(code) => void handleScan(code)} label="Scan" />
              </div>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
            <FormField label="Unit" htmlFor="p_unit" required error={fieldErrors.unit}>
              <select
                id="p_unit"
                className={selectClass(!!fieldErrors.unit)}
                value={form.unit}
                onChange={set('unit')}
                disabled={submitting}
              >
                <option value="">Select unit…</option>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </FormField>
            <FormField label="Schedule" htmlFor="p_schedule">
              <select
                id="p_schedule"
                className={selectClass()}
                value={form.schedule}
                onChange={set('schedule')}
                disabled={submitting}
              >
                <option value="">Unscheduled</option>
                {SCHEDULES.map((n) => <option key={n} value={String(n)}>Schedule {n}</option>)}
              </select>
            </FormField>
          </div>

          <FormField label="Default price (R)" htmlFor="p_price" error={fieldErrors.default_price} className="max-w-[50%]">
            <input
              id="p_price"
              className={inputClass(!!fieldErrors.default_price)}
              type="number"
              min="0"
              step="0.01"
              value={form.default_price}
              onChange={set('default_price')}
              disabled={submitting}
              placeholder="0.00"
            />
          </FormField>

          <FormField label="Notes" htmlFor="p_notes">
            <textarea
              id="p_notes"
              className={textareaClass()}
              value={form.notes}
              onChange={set('notes')}
              disabled={submitting}
              placeholder="Storage instructions, special handling…"
            />
          </FormField>

          <div className="flex gap-3 pt-6 border-t border-border">
            <Button type="submit" size="lg" disabled={submitting || !practiceId}>
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Product'}
            </Button>
            <ButtonLink to="/stock/products" variant="outline" size="lg">Cancel</ButtonLink>
          </div>
        </form>
      </Card>
    </div>
  )
}
